/**
 * Iter106 — Master Console: Claire Knowledge Base manager.
 *
 * CRUD over /api/claire/kb. The KB feeds Claire's RAG retriever (BM25)
 * so every entry here directly improves answer quality.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Sparkles, Trash2, Edit3, X, Save, BookOpen, Check, Wand2,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", content: "", tags: "" };

export default function MasterClaireKB() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(null);   // null = no modal; obj = open
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [tab, setTab] = useState("all"); // 'all' | 'drafts' | 'published'

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/claire/kb`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error("Falha ao carregar Base de Conhecimento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/claire/kb/seed`);
      toast.success(`Seed OK — ${data.inserted} novos · ${data.updated} atualizados · ${data.total} totais`);
      await load();
    } catch (e) {
      toast.error("Falha no seed");
    } finally {
      setSeeding(false);
    }
  };

  const triggerSuggest = async () => {
    if (!window.confirm(
      "Disparar Sugerir KB agora? Vai analisar as top perguntas com 'uncertain=true' dos últimos 7 dias e gerar até 5 drafts via Perplexity.",
    )) return;
    setSuggesting(true);
    try {
      const { data } = await axios.post(`${API}/claire/kb/suggest`);
      toast.success(
        `Auto-suggest concluído — ${data.drafts_created} drafts criados (de ${data.candidates_considered} candidatos).`,
      );
      setTab("drafts");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao sugerir");
    } finally {
      setSuggesting(false);
    }
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API}/claire/kb/${id}/approve`);
      toast.success("Aprovado — agora a Claire usa esta entrada.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aprovar");
    }
  };

  const save = async () => {
    if (!draft?.title?.trim() || !draft?.content?.trim()) {
      toast.error("Título e conteúdo são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const tags = (draft.tags || "")
        .split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { title: draft.title, content: draft.content, tags, source: draft.source || "manual" };
      if (draft.id) {
        await axios.put(`${API}/claire/kb/${draft.id}`, payload);
        toast.success("Atualizado");
      } else {
        await axios.post(`${API}/claire/kb`, payload);
        toast.success("Adicionado");
      }
      setDraft(null);
      await load();
    } catch (e) {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta entrada da Base de Conhecimento?")) return;
    try {
      await axios.delete(`${API}/claire/kb/${id}`);
      toast.success("Excluído");
      await load();
    } catch (e) {
      toast.error("Falha ao excluir");
    }
  };

  const draftsCount = items.filter((i) => i.source === "auto_suggest_draft").length;
  const filtered = items.filter((it) => {
    if (tab === "drafts"    && it.source !== "auto_suggest_draft") return false;
    if (tab === "published" && it.source === "auto_suggest_draft") return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (it.title || "").toLowerCase().includes(q)
        || (it.content || "").toLowerCase().includes(q)
        || (it.tags || []).join(" ").toLowerCase().includes(q);
  });

  return (
    <MasterLayout title="Claire Knowledge Base">
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="master-claire-kb">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B7280]">
                Master Console
              </p>
              <h1 className="font-display text-[28px] font-black text-[#111827] tracking-tight inline-flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#7C3AED]" />
                Base de Conhecimento da Claire
              </h1>
              <p className="text-[13px] text-[#6B7280] mt-1">
                Cada entrada alimenta a RAG (BM25) que a Claire consulta antes de responder.
                Adicione FAQs, especificações de planos, fluxos, troubleshooting…
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={triggerSuggest}
                disabled={suggesting}
                data-testid="kb-suggest-btn"
                title="Gera drafts canônicos para as top perguntas 'uncertain' dos últimos 7 dias"
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB] disabled:opacity-50"
              >
                {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                Sugerir KB
              </button>
              <button
                onClick={seed}
                disabled={seeding}
                data-testid="kb-seed-btn"
                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB] disabled:opacity-50"
              >
                {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />}
                Importar Seeds + Blog
              </button>
              <button
                onClick={() => setDraft({ ...EMPTY })}
                data-testid="kb-new-btn"
                className="h-10 px-4 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white text-[13px] font-bold inline-flex items-center gap-2 hover:from-[#6D28D9] hover:to-[#4338CA]"
              >
                <Plus className="w-4 h-4" /> Nova entrada
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-4 inline-flex rounded-lg border border-[#E5E7EB] overflow-hidden bg-white">
            {[
              { id: "all", label: "Todos", count: items.length },
              { id: "drafts", label: "Drafts auto", count: draftsCount, accent: "text-amber-700" },
              { id: "published", label: "Publicados", count: items.length - draftsCount },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`kb-tab-${t.id}`}
                className={`h-9 px-3 text-[12px] font-semibold border-r last:border-r-0 border-[#E5E7EB] ${
                  tab === t.id
                    ? "bg-[#EDE9FE] text-[#7C3AED]"
                    : "bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
                }`}
              >
                {t.label} <span className={`ml-1 text-[10.5px] ${t.accent || "text-[#9CA3AF]"}`}>({t.count})</span>
              </button>
            ))}
          </div>

          <div className="mb-4">
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título, conteúdo ou tag…"
                data-testid="kb-search"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex items-center justify-center text-[#6B7280]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="al-card p-10 text-center text-[#6B7280]">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-[#D1D5DB]" />
              Nenhuma entrada. Clique <strong>Importar Seeds + Blog</strong> para popular a KB automaticamente.
            </div>
          ) : (
            <div className="grid gap-3" data-testid="kb-list">
              {filtered.map((it) => (
                <article
                  key={it.id}
                  data-testid={`kb-item-${it.id}`}
                  className="al-card p-4 flex items-start gap-4 hover:border-[#7C3AED] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[14.5px] font-bold text-[#111827]">{it.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        it.source === "seed"   ? "bg-[#EDE9FE] text-[#7C3AED]" :
                        it.source === "blog"   ? "bg-[#DBEAFE] text-[#1E40AF]" :
                        it.source === "auto_suggest_draft" ? "bg-amber-100 text-amber-800 ring-1 ring-amber-400" :
                        it.source === "auto_suggest"       ? "bg-emerald-100 text-emerald-800" :
                                                  "bg-[#F3F4F6] text-[#374151]"
                      }`}>
                        {it.source === "auto_suggest_draft" ? "auto · draft" :
                         it.source === "auto_suggest"       ? "auto · publicado" :
                         (it.source || "manual")}
                      </span>
                      {it.suggestion_meta?.uncertain_count > 0 && (
                        <span className="text-[10px] text-[#6B7280]" title="Vezes que essa pergunta apareceu com Claire incerta">
                          {it.suggestion_meta.uncertain_count}× perguntada
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-[#6B7280] leading-relaxed line-clamp-3">
                      {it.content}
                    </p>
                    {(it.tags || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(it.tags || []).map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F9FAFB] border border-[#E5E7EB] text-[#6B7280]">
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {it.source === "auto_suggest_draft" && (
                      <button
                        onClick={() => approve(it.id)}
                        data-testid={`kb-approve-${it.id}`}
                        className="h-8 px-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold inline-flex items-center gap-1"
                        title="Aprovar e ativar para a Claire"
                      >
                        <Check className="w-3.5 h-3.5" /> Aprovar
                      </button>
                    )}
                    <button
                      onClick={() => setDraft({ ...it, tags: (it.tags || []).join(", ") })}
                      data-testid={`kb-edit-${it.id}`}
                      className="w-8 h-8 rounded-lg border border-[#E5E7EB] hover:border-[#7C3AED] hover:text-[#7C3AED] flex items-center justify-center text-[#6B7280]"
                      title="Editar"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      data-testid={`kb-delete-${it.id}`}
                      className="w-8 h-8 rounded-lg border border-[#E5E7EB] hover:border-rose-500 hover:text-rose-500 flex items-center justify-center text-[#6B7280]"
                      title="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

      {/* Drawer-style modal for create/edit. */}
      {draft && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setDraft(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="kb-modal"
            className="al-card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <header className="px-5 py-3 border-b border-[var(--al-border)] flex items-center justify-between">
              <h2 className="text-[14.5px] font-bold">
                {draft.id ? "Editar entrada da KB" : "Nova entrada da KB"}
              </h2>
              <button onClick={() => setDraft(null)} className="text-[#6B7280] hover:text-rose-500">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Título</label>
                <input
                  value={draft.title || ""}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Ex.: Como configurar o webhook do Slack"
                  data-testid="kb-title-input"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">
                  Conteúdo (instruções, FAQ, passo-a-passo)
                </label>
                <textarea
                  value={draft.content || ""}
                  onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  rows={10}
                  placeholder="Texto rico que a Claire vai usar como contexto…"
                  data-testid="kb-content-input"
                  className="mt-1 w-full p-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] leading-relaxed focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Tags (vírgula-separadas)</label>
                <input
                  value={draft.tags || ""}
                  onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                  placeholder="kanban, webhook, slack"
                  data-testid="kb-tags-input"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
            </div>
            <footer className="px-5 py-3 border-t border-[var(--al-border)] flex justify-end gap-2 bg-[#F9FAFB]">
              <button
                onClick={() => setDraft(null)}
                data-testid="kb-cancel"
                className="h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold hover:bg-[#F3F4F6]"
              >Cancelar</button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="kb-save"
                className="h-9 px-4 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 hover:from-[#6D28D9] hover:to-[#4338CA] disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar
              </button>
            </footer>
          </div>
        </div>
      )}
    </MasterLayout>
  );
}
